import { ArrowRight, Lock, MoreHorizontal, Star, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { Investigation } from '@/types/investigation'
import { cn } from '@/utils/cn'
import { InvestigationSourcePill } from './InvestigationDossier'

function StatusPill({ dirty, readOnly }: { dirty: boolean; readOnly: boolean }) {
  const { t } = useTranslation()
  if (readOnly) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
        <Lock className="size-3" />
        {t('investigations.page.source.readOnly')}
      </span>
    )
  }
  if (dirty) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-warning/40 bg-warning/10 px-2.5 py-0.5 text-xs font-medium text-warning">
        <span className="relative inline-flex size-1.5 items-center justify-center">
          <span className="absolute inline-flex size-full animate-ping rounded-full bg-warning opacity-70" />
          <span className="relative inline-flex size-1.5 rounded-full bg-warning" />
        </span>
        {t('investigations.detail.status.modified')}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-success/40 bg-success/10 px-2.5 py-0.5 text-xs font-medium text-success">
      <span className="relative inline-flex size-1.5 rounded-full bg-success" />
      {t('investigations.detail.status.active')}
    </span>
  )
}

// Phone only: the two primary actions drop under the status row and share it
// equally; the overflow menu stays on the status row (see its `order-1` and
// the line break that follows it).
const PHONE_ACTION =
  'max-md:order-2 max-md:h-9 max-md:flex-1 max-md:basis-[calc(50%-0.375rem)] max-md:px-3'

export interface InvestigationDetailHeadingProps {
  inv: Investigation
  dirty: boolean
  togglingReport: boolean
  onOpenInGraph: () => void
  onRequestDelete: () => void
  onToggleReport: () => void
}

export function InvestigationDetailHeading({
  inv,
  dirty,
  togglingReport,
  onOpenInGraph,
  onRequestDelete,
  onToggleReport,
}: InvestigationDetailHeadingProps) {
  const { t } = useTranslation()
  return (
    <div className="space-y-4">
      <span className="text-2xs font-medium uppercase tracking-widest text-muted-foreground/60">
        {t('investigations.detail.eyebrow')}
      </span>
      <h1 className="break-words text-xl font-semibold leading-tight text-foreground md:truncate">
        {inv.title}
      </h1>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm text-muted-foreground md:gap-3">
        <StatusPill dirty={dirty} readOnly={inv.readOnly} />
        <span className="text-border">·</span>
        <InvestigationSourcePill inv={inv} />
        {inv.includedInReport && (
          <>
            <span className="text-border">·</span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/10 px-2.5 py-0.5 text-2xs font-semibold uppercase tracking-wider text-primary">
              <Star className="size-3" />
              {t('investigations.page.featured.inReport')}
            </span>
          </>
        )}
        <div className="contents md:ml-auto md:flex md:items-center md:gap-2">
          <Button
            variant="outline"
            size="sm"
            className={cn('text-primary border-primary/30 hover:bg-primary/5', PHONE_ACTION)}
            onClick={onOpenInGraph}
          >
            <ArrowRight className="size-4" />
            {t('investigations.page.featured.open')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className={PHONE_ACTION}
            onClick={onToggleReport}
            disabled={togglingReport}
            title={
              inv.includedInReport
                ? t('investigations.page.featured.removeFromReport')
                : t('investigations.page.featured.addToReport')
            }
          >
            <Star className={cn('size-4', inv.includedInReport && 'fill-current text-primary')} />
            {inv.includedInReport
              ? t('investigations.page.featured.inReport')
              : t('investigations.page.featured.addToReport')}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                aria-label={t('investigations.page.card.menuLabel')}
                className="size-9 max-md:order-1 max-md:ml-auto md:size-8"
              >
                <MoreHorizontal className="size-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-max md:w-44">
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onSelect={e => {
                  e.preventDefault()
                  onRequestDelete()
                }}
              >
                <Trash2 className="mr-2 size-3.5" />
                {t('investigations.page.card.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {/* Phone only: zero-height line break after the overflow menu, so the
              two actions always start their own row (on a 430px screen the
              first one would otherwise fit beside the status pills). The two
              half row-gaps around it add up to the row's 12px. */}
          <span aria-hidden="true" className="order-1 basis-full md:hidden" />
        </div>
      </div>
    </div>
  )
}
